'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { AuthUI, AUTH_UI_VIEW, getAuthConstants } from '@pixum/auth-core-ui';
import { useDeviceInfo } from 'hooks';
import { useAuthSessionStore } from 'stores/auth';
import { PixumEnvUtils, isBrowser } from 'utils';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  showSidePanel?: boolean;
}

/**
 * Common AuthDialog component for authentication across the application.
 * After successful login, triggers router.refresh() so streaming RSC (SessionStreaming)
 * re-fetches session and user data server-side, updating PIXUM_ENV.USER, and counts the login in the auth session
 * store so the client header slots (cart count, account, user data) reload, whichever component opened the dialog.
 *
 * @param isOpen - Controls visibility of the auth dialog
 * @param onClose - Callback when dialog is closed
 * @param onSuccess - Optional callback after successful authentication
 * @param showSidePanel - Whether to show the side panel in auth UI (default: false)
 */
export function AuthDialog({ isOpen, onClose, onSuccess, showSidePanel = false }: AuthDialogProps) {
  const router = useRouter();
  const registerLogin = useAuthSessionStore(state => state.registerLogin);
  const { isMobile } = useDeviceInfo();
  const CDN_HOST = isBrowser() ? PixumEnvUtils.get('CDN_HOST') : '';
  const authConstants = getAuthConstants();

  const handleAuthSuccess = useCallback(() => {
    onClose();
    router.refresh();
    registerLogin();

    if (onSuccess) {
      onSuccess();
    }
  }, [onSuccess, onClose, router, registerLogin]);

  if (!isOpen) {
    return null;
  }

  return (
    <AuthUI
      cdnHost={CDN_HOST as string}
      constants={authConstants}
      defaultView={AUTH_UI_VIEW.DIALOG_VIEW}
      isMobile={isMobile}
      onClose={onClose}
      onLoginSuccess={handleAuthSuccess}
      onSignupSuccess={handleAuthSuccess}
      showSidePanel={showSidePanel}
    />
  );
}
